import { adminRoutes, getCategoryByPath, getSectionByPath } from './adminRoutes';

const STORAGE_PREFIX = 'owaza.admin-place.v1';
const STORAGE_VERSION = 1;

// The first value of each list is the page's default view, i.e. what the page
// shows when the query parameter is absent or invalid.
const QUERY_STATE = {
  customers: { name: 'view', values: ['customers', 'settings'] },
  newsletter: {
    name: 'tab',
    values: ['lists', 'recipients', 'segments', 'automations', 'newsletters'],
  },
  socialmedia: {
    name: 'tab',
    values: ['all', 'draft', 'pending_review', 'approved', 'scheduled', 'published'],
  },
};

const normalizePath = (path) => path.replace(/\/+$/, '') || '/';

// SHA-256 keeps raw IDs/emails out of the storage key and avoids the practical
// collisions of the previous 32-bit namespace. This is namespacing, not
// authentication; authorization is always re-checked before a value is used.
const SHA256_CONSTANTS = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotateRight = (value, amount) => (value >>> amount) | (value << (32 - amount));

const hashIdentity = (value) => {
  const input = new TextEncoder().encode(value);
  const paddedLength = Math.ceil((input.length + 9) / 64) * 64;
  const bytes = new Uint8Array(paddedLength);
  bytes.set(input);
  bytes[input.length] = 0x80;

  const view = new DataView(bytes.buffer);
  const bitLength = input.length * 8;
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  const state = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const words = new Uint32Array(64);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4, false);
    }
    for (let index = 16; index < 64; index += 1) {
      const previous15 = words[index - 15];
      const previous2 = words[index - 2];
      const sigma0 = rotateRight(previous15, 7)
        ^ rotateRight(previous15, 18) ^ (previous15 >>> 3);
      const sigma1 = rotateRight(previous2, 17)
        ^ rotateRight(previous2, 19) ^ (previous2 >>> 10);
      words[index] = (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = state;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + sum1 + choice + SHA256_CONSTANTS[index] + words[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    state[0] = (state[0] + a) >>> 0;
    state[1] = (state[1] + b) >>> 0;
    state[2] = (state[2] + c) >>> 0;
    state[3] = (state[3] + d) >>> 0;
    state[4] = (state[4] + e) >>> 0;
    state[5] = (state[5] + f) >>> 0;
    state[6] = (state[6] + g) >>> 0;
    state[7] = (state[7] + h) >>> 0;
  }

  return state.map((word) => word.toString(16).padStart(8, '0')).join('');
};

export const getAdminPlaceIdentity = (adminUser) => {
  const id = [adminUser?.id, adminUser?._id].find(
    (candidate) => (typeof candidate === 'string' || typeof candidate === 'number')
      && String(candidate).trim(),
  );
  if (id !== undefined) {
    return hashIdentity(String(id));
  }

  const email = adminUser?.email;
  if (typeof email !== 'string' || !email.trim()) return null;
  return hashIdentity(email.trim().toLowerCase());
};

export const getAdminPlaceStorageKey = (adminUser) => {
  const identity = getAdminPlaceIdentity(adminUser);
  return identity ? `${STORAGE_PREFIX}.${identity}` : null;
};

const emptyMemory = () => ({ version: STORAGE_VERSION, cockpits: {}, pages: {} });

const readMemory = (adminUser, storage = window.localStorage) => {
  const key = getAdminPlaceStorageKey(adminUser);
  if (!key) return emptyMemory();

  try {
    const parsed = JSON.parse(storage.getItem(key));
    if (
      parsed?.version !== STORAGE_VERSION
      || !parsed.cockpits
      || typeof parsed.cockpits !== 'object'
      || Array.isArray(parsed.cockpits)
      || !parsed.pages
      || typeof parsed.pages !== 'object'
      || Array.isArray(parsed.pages)
    ) {
      return emptyMemory();
    }
    return parsed;
  } catch {
    return emptyMemory();
  }
};

const writeMemory = (adminUser, memory, storage = window.localStorage) => {
  const key = getAdminPlaceStorageKey(adminUser);
  if (!key) return;
  try {
    storage.setItem(key, JSON.stringify(memory));
  } catch {
    // Private browsing/quota failures must never block navigation.
  }
};

// The view the canonical page actually displays for this URL, so a queryless
// visit (or Back to one) records the default instead of leaving older state.
const displayedQueryValue = (section, search) => {
  const config = QUERY_STATE[section.id];
  if (!config) return null;
  const value = new URLSearchParams(search).get(config.name);
  return config.values.includes(value) ? value : config.values[0];
};

// Returns true when the stored place changed, so navigation surfaces that
// resolved their destinations during render can refresh.
export const rememberAdminLocation = (
  { pathname, search = '' },
  adminUser,
  canAccessSection,
  storage = window.localStorage,
) => {
  const section = getSectionByPath(pathname);
  const categoryId = getCategoryByPath(pathname);
  if (!section || !categoryId || !canAccessSection(section.id)) return false;

  const category = adminRoutes[categoryId];
  if (!category?.sections.some((candidate) => candidate.id === section.id)) return false;
  if (!getAdminPlaceStorageKey(adminUser)) return false;

  const memory = readMemory(adminUser, storage);
  const previous = JSON.stringify(memory);
  memory.cockpits[categoryId] = section.id;

  // Query state only belongs to the canonical page. Nested editor/detail URLs
  // can contain record IDs or action state and are deliberately not persisted.
  if (normalizePath(pathname) === section.path) {
    const queryValue = displayedQueryValue(section, search);
    if (queryValue) memory.pages[section.id] = queryValue;
  }

  if (JSON.stringify(memory) === previous) return false;
  writeMemory(adminUser, memory, storage);
  return true;
};

export const getRememberedSectionPath = (
  section,
  adminUser,
  storage = window.localStorage,
) => {
  const config = QUERY_STATE[section?.id];
  if (!section || !config) return section?.path || null;

  const remembered = readMemory(adminUser, storage).pages[section.id];
  if (!config.values.includes(remembered)) return section.path;
  return `${section.path}?${config.name}=${encodeURIComponent(remembered)}`;
};

export const getRememberedCockpitPath = (
  categoryId,
  accessibleSections,
  adminUser,
  storage = window.localStorage,
) => {
  if (!accessibleSections?.length) return '/admin';

  const rememberedId = readMemory(adminUser, storage).cockpits[categoryId];
  const section = accessibleSections.find((candidate) => candidate.id === rememberedId)
    || accessibleSections[0];
  return getRememberedSectionPath(section, adminUser, storage);
};

export const getRememberedCategoryPath = (
  categoryId,
  adminUser,
  canAccessSection,
  storage = window.localStorage,
) => {
  const category = adminRoutes[categoryId];
  if (!category || typeof canAccessSection !== 'function') return '/admin';

  const accessibleSections = category.sections.filter(
    (section) => canAccessSection(section.id),
  );
  return getRememberedCockpitPath(categoryId, accessibleSections, adminUser, storage);
};

export const getRememberedPageValue = (
  scope,
  allowedValues,
  fallback,
  adminUser,
  storage = window.localStorage,
) => {
  const value = readMemory(adminUser, storage).pages[scope];
  return allowedValues.includes(value) ? value : fallback;
};

export const rememberPageValue = (
  scope,
  value,
  allowedValues,
  adminUser,
  storage = window.localStorage,
) => {
  if (!scope || !allowedValues.includes(value)) return;
  const memory = readMemory(adminUser, storage);
  memory.pages[scope] = value;
  writeMemory(adminUser, memory, storage);
};
